import { CircleMinus, CirclePlus, RotateCcw, ScrollText, Search, SearchX, Store } from "lucide-react";
import type { ReactNode } from "react";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import Select from "@/components/ui/Select";
import { TableSkeleton } from "@/components/ui/Skeleton";
import type { AdminWalletRow as WalletRecord } from "@/types/wallet";
import { formatDate, formatINR } from "@/pages/retailer/wallet/data";
import { BALANCE_FILTERS, BALANCE_STATUS_META, availableColor, initials, tierFromStatus, walletAvailable } from "../data";
import { usePermission } from "@/context/PermissionContext";

export interface WalletRow extends WalletRecord {
  lastRechargeAmount: number;
}

interface WalletsTabProps {
  loading: boolean;
  rows: WalletRow[];
  search: string;
  onSearch: (v: string) => void;
  balance: string;
  onBalance: (v: string) => void;
  onReset: () => void;
  onOpenLedger: (retailerId: string) => void;
  onAddBalance: (retailerId: string) => void;
  onDeductBalance: (retailerId: string) => void;
  onViewRetailer: (retailerId: string) => void;
}

function MenuAction({ icon, children, onClick, danger }: { icon: ReactNode; children: ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50 cursor-pointer ${danger ? "text-danger" : "text-ink"}`}
    >
      {icon}
      {children}
    </button>
  );
}

export default function WalletsTab(props: WalletsTabProps) {
  const { loading, rows, search, onSearch, balance, onBalance, onReset, onOpenLedger, onAddBalance, onDeductBalance, onViewRetailer } = props;

  const columns: Column<WalletRow>[] = [
    {
      key: "retailerId",
      header: "Retailer",
      render: (row) => {

        return (
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-500">
              {initials(row.name || row.retailerId)}
            </span>
            <div>
              <p className="text-sm font-medium text-ink">{row.shopName || row.name || "Retailer"}</p>
              <p className="text-xs text-ink-secondary">
                {[row.name, row.userCode || (!row.retailerId?.includes("-") ? row.retailerId : null)].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: "available",
      header: "Available Balance",
      align: "right",
      render: (row) => (
        <span className={`whitespace-nowrap text-sm font-semibold ${availableColor(walletAvailable(row))}`}>
          {formatINR(walletAvailable(row))}
        </span>
      ),
    },
    {
      key: "totalBalance",
      header: "Total Balance",
      align: "right",
      render: (row) => <span className="whitespace-nowrap text-sm text-ink">{formatINR(row.totalBalance)}</span>,
    },
    {
      key: "lifetimeSpent",
      header: "Lifetime Spent",
      align: "right",
      render: (row) => <span className="whitespace-nowrap text-sm text-ink">{formatINR(row.lifetimeSpent)}</span>,
    },
    {
      key: "lastRechargeAt",
      header: "Last Recharge",
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{row.lastRechargeAt ? formatDate(row.lastRechargeAt) : "—"}</p>
          <p className="text-xs text-ink-secondary">{row.lastRechargeAmount > 0 ? formatINR(row.lastRechargeAmount) : "No recharge"}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const meta = BALANCE_STATUS_META[tierFromStatus(row.status)];
        return (
          <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.bg} ${meta.text}`}>
            {meta.label}
          </span>
        );
      },
    },
  ];

  const { canCreate } = usePermission();

  function rowActions(row: WalletRow) {
    return (
      <>
        {canCreate("wallets") && (
          <>
            <MenuAction icon={<CirclePlus className="h-4 w-4 text-ink-muted" />} onClick={() => onAddBalance(row.retailerId)}>
              Add Balance
            </MenuAction>
            <MenuAction icon={<CircleMinus className="h-4 w-4 text-ink-muted" />} onClick={() => onDeductBalance(row.retailerId)}>
              Deduct Balance
            </MenuAction>
          </>
        )}
        <MenuAction icon={<ScrollText className="h-4 w-4 text-ink-muted" />} onClick={() => onOpenLedger(row.retailerId)}>
          View Ledger
        </MenuAction>
        <MenuAction icon={<Store className="h-4 w-4 text-ink-muted" />} onClick={() => onViewRetailer(row.retailerId)}>
          View Retailer
        </MenuAction>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-[10px] border border-line bg-card p-4 shadow-card lg:flex-row lg:items-center">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by shop name, owner or retailer ID"
              className="h-[42px] w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500/30"
            />
          </div>
          <div className="sm:w-52">
            <Select value={balance} onChange={(e) => onBalance(e.target.value)} options={BALANCE_FILTERS} />
          </div>
        </div>
        <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
          Reset
        </Button>
      </div>
      {loading ? (
        <TableSkeleton rows={5} columns={7} />
      ) : rows.length === 0 ? (
        <EmptyState icon={SearchX} title="No wallets found" description="Try adjusting your search or balance filter." actionLabel="Reset Filters" onAction={onReset} />
      ) : (
        <DataTable columns={columns} data={rows} rowKey={(r) => r.retailerId} actions={rowActions} onRowClick={(row) => onOpenLedger(row.retailerId)} pageSize={10} pageSizeOptions={[10, 25, 50]} />
      )}
    </div>
  );
}